import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditBooking = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebooking, setRebooking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    message: '',
    scheduled_date: '',
    scheduled_time: '',
    notes: ''
  });

  // Get today's date for min date validation
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`);
      const bookingData = response.data;
      setBooking(bookingData);
      setFormData({
        message: bookingData.message || '',
        scheduled_date: bookingData.scheduled_date || '',
        scheduled_time: bookingData.scheduled_time || '',
        notes: bookingData.notes || ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const params = new URLSearchParams();
      if (formData.message) params.append('message', formData.message);
      if (formData.scheduled_date) params.append('scheduled_date', formData.scheduled_date);
      if (formData.scheduled_time) params.append('scheduled_time', formData.scheduled_time);
      if (formData.notes) params.append('notes', formData.notes);

      await axios.put(`${API}/bookings/${id}/edit?${params.toString()}`);
      setSuccess('Booking updated successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await axios.delete(`${API}/bookings/${id}`);
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleRebook = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.scheduled_date) {
      setError('Please select a date for your new booking');
      return;
    }
    if (!formData.scheduled_time) {
      setError('Please select a time for your new booking');
      return;
    }
    
    setError('');
    setRebooking(true);
    
    // For cancelled/rejected bookings, create a new booking with the updated details
    try {
      await axios.post(`${API}/bookings`, {
        service_id: booking.service_id,
        message: formData.message || booking.message,
        booking_date: formData.scheduled_date,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time
      });
      
      // Delete the old cancelled booking after successful creation
      await axios.delete(`${API}/bookings/${id}`);
      
      setSuccess('New booking created successfully! Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create new booking');
    } finally {
      setRebooking(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this booking? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/bookings/${id}`);
      setSuccess('Booking deleted successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const canEdit = booking && ['pending', 'accepted'].includes(booking.status);
  const canRebook = booking && ['cancelled', 'rejected'].includes(booking.status) && booking.client_id === user?.id;
  const canCancel = booking && booking.status === 'pending' && booking.client_id === user?.id;
  const canDelete = booking && ['cancelled', 'rejected'].includes(booking.status) && booking.client_id === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Booking Not Found</h2>
            <p className="text-gray-600">The booking you&apos;re looking for doesn&apos;t exist.</p>
          </div>
        </div>
      </div>
    );
  }

  if (booking.provider_id !== user?.id && booking.client_id !== user?.id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">You can only edit your own bookings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="edit-booking-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
          Edit Booking
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="success-message">
            {success}
          </div>
        )}

        {/* Booking Info Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Booking Details</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Service:</span>
              <p className="font-semibold">{booking.service_title}</p>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <p className={`font-semibold ${
                booking.status === 'pending' ? 'text-yellow-600' :
                booking.status === 'accepted' ? 'text-green-600' :
                booking.status === 'completed' ? 'text-blue-600' :
                'text-gray-600'
              }`}>
                {booking.status.replace('_', ' ')}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Client:</span>
              <p className="font-semibold">{booking.client_name}</p>
            </div>
            <div>
              <span className="text-gray-500">Provider:</span>
              <p className="font-semibold">{booking.provider_name}</p>
            </div>
            <div>
              <span className="text-gray-500">Price:</span>
              <p className="font-semibold text-blue-600">{({ PHP: '₱', USD: '$', EUR: '€', GBP: '£' }[booking.currency] || booking.currency || '₱')}{Number(booking.price).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Re-booking Form for Cancelled/Rejected Bookings */}
          {canRebook ? (
            <div>
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-bold text-yellow-800 flex items-center gap-2">
                  <span>🔄</span> Re-book this Service
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This booking was {booking.status}. You can submit a new booking request with updated details below.
                </p>
              </div>

              <form onSubmit={handleRebook}>
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold mb-2">Message / Instructions</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Add any special instructions or notes for the provider..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="rebook-message-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      New Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="scheduled_date"
                      value={formData.scheduled_date}
                      onChange={handleChange}
                      min={today}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="rebook-date-input"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">
                      New Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="scheduled_time"
                      value={formData.scheduled_time}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="rebook-time-input"
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                    data-testid="rebook-cancel-btn"
                  >
                    Back to Dashboard
                  </button>
                  <button
                    type="submit"
                    disabled={rebooking}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                    data-testid="rebook-submit-btn"
                  >
                    {rebooking ? 'Submitting...' : '🔄 Submit New Booking'}
                  </button>
                </div>
              </form>

              {canDelete && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-500 mb-3">
                    Or permanently remove this booking from your history:
                  </p>
                  <button
                    onClick={handleDelete}
                    className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold"
                    data-testid="delete-booking-btn"
                  >
                    🗑️ Delete Booking Permanently
                  </button>
                </div>
              )}
            </div>
          ) : !canEdit ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                This booking cannot be edited because its status is &quot;{booking.status.replace('_', ' ')}&quot;.
              </p>
              <p className="text-sm text-gray-500">
                Only bookings with &quot;pending&quot; or &quot;accepted&quot; status can be modified.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Back to Dashboard
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">Message / Instructions</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Add any special instructions or notes for the provider..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="booking-message-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-bold mb-2">Scheduled Date</label>
                  <input
                    type="date"
                    name="scheduled_date"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="booking-date-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">Scheduled Time</label>
                  <input
                    type="time"
                    name="scheduled_time"
                    value={formData.scheduled_time}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="booking-time-input"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">Additional Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Any additional notes..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="booking-notes-input"
                />
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                  data-testid="save-booking-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {canCancel && (
            <div className="mt-6 pt-6 border-t">
              <button
                onClick={handleCancel}
                className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold"
                data-testid="cancel-booking-btn"
              >
                Cancel Booking
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EditBooking;
